import React, { useEffect, useState } from "react";
import { Col, Container, Row, Spinner, Stack } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { setPageNameHeader } from "src/redux/actions/header";
import { getUserData } from "src/redux/selectors/user";
import { FirebaseDataService } from "src/services/Firebase/data";
import { colors, Content, ContentFrame, TopBar } from "src/styles";
import styled from "styled-components";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import BreadCrumbs from "src/components/BreadCrumbs";

interface KPIData {
  totalSpend: number;
  spendByService: ServiceSpend[];
}

interface ServiceSpend {
  serviceId: string;
  serviceName: string;
  amount: number;
  percentage: number;
}

const KPI_BREADCRUMBS = [
  {
    id: "/",
    name: "Home",
  },
  {
    id: "/kpi",
    name: "KPI Dashboard",
  },
];

const KPI = () => {
  const dispatch = useDispatch();
  const { id: loggedInUserId, isManager } = useSelector(getUserData);
  const [data, setData] = useState<KPIData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState<Date | null>(moment().startOf("month").toDate());
  const [dateTo, setDateTo] = useState<Date | null>(new Date());

  const fetchKPIData = async () => {
    setIsLoading(true);
    try {
      if (!dateFrom || !dateTo) return;

      const fromDate = moment(dateFrom).format("YYYY-MM-DD");
      const toDate = moment(dateTo).format("YYYY-MM-DD");

      // Replace with actual API call
      const { data: kpiData } = await FirebaseDataService.getKPIData(loggedInUserId, isManager, fromDate, toDate);

      setData(kpiData);
    } catch (error) {
      console.error("Error fetching KPI data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    dispatch(setPageNameHeader("KPI Dashboard"));
    return () => {
      dispatch(setPageNameHeader(""));
    };
  }, [dispatch]);

  useEffect(() => {
    fetchKPIData();
  }, [dateFrom, dateTo]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  return (
    <Wrapper fluid>
      <TopBar>
        <BreadCrumbs folderStack={KPI_BREADCRUMBS} onClickItem={() => {}} />
      </TopBar>

      <Content>
        <ContentFrame>
          <PageHeader>
            <Title>KPI Dashboard</Title>
            <DateFilterContainer>
              <DateFilterLabel>From:</DateFilterLabel>
              <StyledDatePicker
                selected={dateFrom}
                onChange={(date: Date) => setDateFrom(date)}
                dateFormat="MM/dd/yyyy"
                placeholderText="Start Date"
                maxDate={dateTo || new Date()}
              />
              <DateFilterLabel>To:</DateFilterLabel>
              <StyledDatePicker
                selected={dateTo}
                onChange={(date: Date) => setDateTo(date)}
                dateFormat="MM/dd/yyyy"
                placeholderText="End Date"
                minDate={dateFrom}
                maxDate={new Date()}
              />
            </DateFilterContainer>
          </PageHeader>

          {isLoading ? (
            <SpinnerContainer>
              <Spinner animation="border" />
            </SpinnerContainer>
          ) : (
            <Stack gap={4}>
              {/* Total Spend Section */}
              <KPICard>
                <CardHeader>
                  <CardTitle>Total Money Spent</CardTitle>
                  <DateRange>
                    {moment(dateFrom).format("MMM DD, YYYY")} - {moment(dateTo).format("MMM DD, YYYY")}
                  </DateRange>
                </CardHeader>
                <CardBody>
                  <TotalAmount>{formatCurrency(data?.totalSpend || 0)}</TotalAmount>
                </CardBody>
              </KPICard>

              {/* Spend by Service Section */}
              <KPICard>
                <CardHeader>
                  <CardTitle>Spend by Service</CardTitle>
                </CardHeader>
                <CardBody>
                  {data?.spendByService && data.spendByService.length > 0 ? (
                    <ServiceTable>
                      <ServiceTableHeader>
                        <HeaderCell>Service Name</HeaderCell>
                        <HeaderCell align="right">Amount</HeaderCell>
                        <HeaderCell align="right">Percentage</HeaderCell>
                      </ServiceTableHeader>
                      <ServiceTableBody>
                        {data.spendByService.map((service, index) => (
                          <ServiceRow key={service.serviceId || index}>
                            <ServiceCell>{service.serviceName}</ServiceCell>
                            <ServiceCell align="right">
                              <Amount>{formatCurrency(service.amount)}</Amount>
                            </ServiceCell>
                            <ServiceCell align="right">
                              <Stack direction="horizontal" gap={2} style={{ justifyContent: "flex-end" }}>
                                <ProgressContainer>
                                  <ProgressBar $percentage={service.percentage} />
                                </ProgressContainer>
                                <Percentage>{service.percentage.toFixed(1)}%</Percentage>
                              </Stack>
                            </ServiceCell>
                          </ServiceRow>
                        ))}
                      </ServiceTableBody>
                    </ServiceTable>
                  ) : (
                    <NoDataMessage>No service data available for the selected period</NoDataMessage>
                  )}
                </CardBody>
              </KPICard>

              {/* Summary Statistics */}
              <Row>
                <Col md={4}>
                  <StatCard>
                    <StatLabel>Average per Service</StatLabel>
                    <StatValue>
                      {formatCurrency(
                        data?.spendByService && data.spendByService.length > 0
                          ? data.totalSpend / data.spendByService.length
                          : 0,
                      )}
                    </StatValue>
                  </StatCard>
                </Col>
                <Col md={4}>
                  <StatCard>
                    <StatLabel>Number of Services</StatLabel>
                    <StatValue>{data?.spendByService?.length || 0}</StatValue>
                  </StatCard>
                </Col>
                <Col md={4}>
                  <StatCard>
                    <StatLabel>Highest Service Spend</StatLabel>
                    <StatValue>
                      {data?.spendByService && data.spendByService.length > 0
                        ? formatCurrency(Math.max(...data.spendByService.map((s) => s.amount)))
                        : formatCurrency(0)}
                    </StatValue>
                  </StatCard>
                </Col>
              </Row>
            </Stack>
          )}
        </ContentFrame>
      </Content>
    </Wrapper>
  );
};

export default KPI;

// Styled Components
const Wrapper = styled(Container)`
  padding: 30px 20px;
  padding-top: 15px;
  & > div:first-child {
    margin: 0;
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 16px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: ${colors.grey.dark};
  margin: 0;
`;

const DateFilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const DateFilterLabel = styled.span`
  font-size: 14px;
  color: ${colors.grey.dark};
  font-weight: 500;
`;

const StyledDatePicker = styled(DatePicker)`
  padding: 8px 12px;
  border: 1px solid ${colors.grey.light11};
  border-radius: 4px;
  font-size: 14px;
  width: 140px;
  outline: none;

  &:focus {
    border-color: ${colors.kleenway.green};
  }

  &::placeholder {
    color: ${colors.grey.light10};
  }
`;

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const KPICard = styled.div`
  background: ${colors.white.default};
  border: 1px solid ${colors.grey.light11};
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.05);
`;

const CardHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 2px solid ${colors.kleenway.green};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CardTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${colors.grey.dark};
  margin: 0;
`;

const DateRange = styled.span`
  font-size: 14px;
  color: ${colors.grey.light10};
`;

const CardBody = styled.div`
  padding: 24px;
`;

const TotalAmount = styled.div`
  font-size: 48px;
  font-weight: 700;
  color: ${colors.kleenway.green};
  text-align: center;
`;

const ServiceTable = styled.div`
  width: 100%;
`;

const ServiceTableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 2fr;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 2px solid ${colors.grey.light11};
  margin-bottom: 8px;
`;

const HeaderCell = styled.div<{ align?: string }>`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.grey.dark};
  text-align: ${({ align }) => align || "left"};
`;

const ServiceTableBody = styled.div`
  display: flex;
  flex-direction: column;
`;

const ServiceRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 2fr;
  gap: 16px;
  padding: 16px 0;
  border-bottom: 1px solid ${colors.grey.light11};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${colors.grey.light2};
  }
`;

const ServiceCell = styled.div<{ align?: string }>`
  font-size: 14px;
  color: ${colors.grey.dark};
  text-align: ${({ align }) => align || "left"};
  display: flex;
  align-items: center;
  ${({ align }) => align === "right" && "justify-content: flex-end;"}
`;

const Amount = styled.span`
  font-weight: 600;
  color: ${colors.black.light1};
`;

const Percentage = styled.span`
  font-weight: 500;
  min-width: 50px;
  text-align: right;
`;

const ProgressContainer = styled.div`
  flex: 1;
  max-width: 150px;
  height: 8px;
  background-color: ${colors.grey.light3};
  border-radius: 4px;
  overflow: hidden;
`;

const ProgressBar = styled.div<{ $percentage: number }>`
  width: ${({ $percentage }) => $percentage}%;
  height: 100%;
  background-color: ${colors.kleenway.green};
  border-radius: 4px;
  transition: width 0.3s ease;
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: ${colors.grey.light10};
  font-size: 16px;
`;

const StatCard = styled.div`
  background: ${colors.white.default};
  border: 1px solid ${colors.grey.light11};
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.05);
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: ${colors.black.light1};
  margin-bottom: 8px;
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: ${colors.grey.dark};
`;
